import {
	AbstractControl,
	FormGroup,
	ValidationErrors,
	ValidatorFn,
} from '@angular/forms';

export class PasswordUtils {
	public static PASSWORD_REGEX: RegExp = new RegExp(
		/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()\-=+[\]{}|;:,.<>?])[A-Za-z\d!@#$%^&*()\-=+[\]{}|;:,.<>?]{8,}$/
	);
	public static PASSWORD_MIN_LENGTH: number = 8;

	private static PASSWORD_LOWERCASE_REGEX: RegExp = new RegExp(/[a-z]/);
	private static PASSWORD_UPPERCASE_REGEX: RegExp = new RegExp(/[A-Z]/);
	private static PASSWORD_NUMBER_REGEX: RegExp = new RegExp(/\d/);
	private static PASSWORD_SPECIAL_CHARACTER_REGEX: RegExp = new RegExp(
		/[@$!%*?&.-]/
	);

	static passwordValidationFn(
		control: AbstractControl
	): ValidationErrors | null {
		const value = control.value;
		let errors: any = {};

		if (!value) {
			return { required: true };
		}

		errors = PasswordUtils.getPasswordPatternErrors(value);

		return Object.keys(errors).length
			? { pattern: PasswordUtils.getFirstErrorMessageAvailable(errors) }
			: null;
	}

	static getPasswordPatternErrors(value: string): object {
		let errors: any = {};

		if (value.length < this.PASSWORD_MIN_LENGTH) {
			errors['minLength'] = 'general.password_min_length_error';
		}

		if (!this.PASSWORD_LOWERCASE_REGEX.test(value)) {
			errors['lowercase'] = 'general.password_lowercase_error';
		}

		if (!this.PASSWORD_UPPERCASE_REGEX.test(value)) {
			errors['uppercase'] = 'general.password_uppercase_error';
		}

		if (!this.PASSWORD_NUMBER_REGEX.test(value)) {
			errors['number'] = 'general.password_number_error';
		}

		if (!this.PASSWORD_SPECIAL_CHARACTER_REGEX.test(value)) {
			errors['specialChar'] = 'general.password_special_character_error';
		}

		return errors;
	}

	static getFirstErrorMessageAvailable(errors: object): string {
		const errorMessages = Object.values(errors);

		if (!errorMessages.length) {
			return null;
		}

		return errorMessages[0];
	}

	static matchPasswordsValidator: ValidatorFn = (formGroup: AbstractControl): null => {
		const password = formGroup.get('password');
		const confirmPassword = formGroup.get('confirmPassword');

		if (!password || !confirmPassword) {
			return null;
		}

		if (password.value !== confirmPassword.value) {
			confirmPassword.setErrors({ passwordsMismatch: true });
		} else {
			if (confirmPassword.hasError('passwordsMismatch')) {
				confirmPassword.setErrors(null);
			}
		}

		return null;
	};

	static getControlError(
		form: FormGroup,
		property: { field: string; label: string; length: string }
	): string {
		const control = form.get(property?.field);

		if (!control) {
			return null;
		}

		if (control?.hasError('required')) {
			return 'general.field_required';
		}
		if (control?.hasError('pattern')) {
			return PasswordUtils.getFirstErrorMessageAvailable(
				PasswordUtils.getPasswordPatternErrors(control.value)
			);
		}
		if (
			control?.hasError('passwordsMismatch') &&
			property?.field === 'confirmPassword'
		) {
			return 'register.passwords_do_not_match';
		}

		return null;
	}

	static matchPassword(passwordField: string, confirmPasswordField: string): ValidatorFn {
		return (formGroup: AbstractControl): ValidationErrors | null => {
		  const passwordControl = formGroup.get(passwordField);
		  const confirmPasswordControl = formGroup.get(confirmPasswordField);
		  if (!passwordControl || !confirmPasswordControl) {
			return null;
		  }
		  if (confirmPasswordControl.errors && !confirmPasswordControl.errors['passwordMismatch']) {
			return null;
		  }
		  const passwordsMatch = passwordControl.value === confirmPasswordControl.value;
		  if (!passwordsMatch) {
			const errorMessage = 'register.password_mismatch';
			confirmPasswordControl.setErrors({ passwordMismatch: errorMessage });
			return { passwordMismatch: errorMessage };
		  } else {
			confirmPasswordControl.setErrors(null);
			return null;
		  }
		};
	}
}
